"use client";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import { ChevronRight } from "lucide-react";

function ShopBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div data-section-id="5244" className="flex items-center gap-2">
        <div className="h-4 w-16 bg-gray-200 animate-pulse rounded-md" />
        <div className="h-4 w-4 bg-gray-200 animate-pulse rounded-md" />
        <div className="h-4 w-24 bg-gray-200 animate-pulse rounded-md" />
      </div>
    );
  }

  if (error || links.length === 0) return null;

  const lastIndex = links.length - 1;
  const currentTitle = links[lastIndex]?.title ?? "Archive";

  return (
    <div className="flex flex-col items-start gap-8 md:gap-10">
      <Breadcrumb>
        <BreadcrumbList className="text-xs uppercase tracking-widest font-sans text-gray-500">
          {links.map((link, i) => (
            <BreadcrumbItem
              key={`${link.href}-${i}`}
              data-index={i}
              className="inline-flex items-center gap-1.5"
            >
              {i === lastIndex ? (
                <BreadcrumbPage className="text-[#e63946] font-semibold">
                  {link.title}
                </BreadcrumbPage>
              ) : (
                <>
                  <BreadcrumbLink
                    href={link.href}
                    className="hover:text-black transition-colors duration-200"
                  >
                    {link.title}
                  </BreadcrumbLink>
                  <BreadcrumbSeparator className="text-gray-400">
                    <ChevronRight className="h-3 w-3" />
                  </BreadcrumbSeparator>
                </>
              )}
            </BreadcrumbItem>
          ))}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex flex-col gap-6 w-full">
        <div className="h-px w-16 bg-[#e63946]" aria-hidden="true" />
        <h1
          className="font-sans font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-black"
        >
          {currentTitle}
        </h1>
        <p className="max-w-2xl text-lg text-gray-600 leading-relaxed font-sans">
          Explore our premium selection of <span className="text-black font-semibold">{currentTitle}</span>. 
          ProWhip provides high-performance culinary tools designed for professional results and 
          everyday kitchen innovation.
        </p>
      </div>
    </div>
  );
}

export default function () {
  return (
    <section
      id="archive-title"
      className="bg-white text-black relative overflow-hidden border-b border-gray-200"
    >
      {}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-lg bg-red-50 blur-3xl" />
        <div className="absolute -bottom-40 -left-20 h-80 w-80 rounded-lg bg-gray-100 blur-3xl" />
      </div>

      {}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 md:py-28 lg:py-32">
        <div className="flex items-start justify-between gap-8 flex-wrap">
          <div className="flex-1 min-w-0">
            <ShopBreadcrumbsProvider>
              <ShopBreadcrumbsConsumer />
            </ShopBreadcrumbsProvider>
          </div>

          <div className="hidden lg:flex flex-col items-end gap-3 shrink-0">
            <span className="text-[10px] uppercase tracking-[0.3em] font-sans text-gray-500">
              PROWHIP
            </span>
            <span className="text-[10px] uppercase tracking-[0.3em] font-sans text-gray-400">
              Culinary Essentials
            </span>
            <div className="h-px w-24 bg-gray-300" />
          </div>
        </div>
      </div>
    </section>
  );
}